import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dices, Gift, History, Lock, MapPin, MessageSquareText, Package, Sparkles, Ticket, Truck, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";

const GOLD = "#D4AF37";
const STATUS_STYLE = {
  PENDING_ADDRESS: "text-amber-300 bg-amber-400/10 border-amber-400/30",
  PROCESSING: "text-sky-300 bg-sky-400/10 border-sky-400/30",
  SHIPPED: "text-violet-300 bg-violet-400/10 border-violet-400/30",
  COMPLETED: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
};
const BLANK_NAME = "BETTER LUCK NEXT TIME";

function Wheel({ segments, targetIndex, spinning, onDone }) {
  const [rotation, setRotation] = useState(0);
  const n = segments.length;
  const seg = 360 / n;
  const R = 150;
  const C = 160;

  useEffect(() => {
    if (!spinning || targetIndex == null) return;
    const center = targetIndex * seg + seg / 2;
    const target = 360 * 6 + ((360 - center) % 360);
    setRotation((r) => r - (r % 360) + target);
    const t = setTimeout(onDone, 5200);
    return () => clearTimeout(t);
  }, [spinning, targetIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative w-[320px] h-[320px] mx-auto" data-testid="spin-wheel">
      <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-20" style={{ filter: "drop-shadow(0 2px 6px rgba(212,175,55,.6))" }}>
        <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent" style={{ borderTopColor: GOLD }} />
      </div>
      <div
        className="w-full h-full rounded-full border-4 shadow-[0_0_60px_rgba(212,175,55,.25)]"
        style={{
          borderColor: "rgba(212,175,55,.5)",
          transform: `rotate(${rotation}deg)`,
          transition: spinning ? "transform 5s cubic-bezier(0.12,0.8,0.08,1)" : "none",
        }}
      >
        <svg viewBox="0 0 320 320" className="w-full h-full">
          {segments.map((s, i) => {
            const a0 = ((i * seg - 90) * Math.PI) / 180;
            const a1 = (((i + 1) * seg - 90) * Math.PI) / 180;
            const x0 = C + R * Math.cos(a0), y0 = C + R * Math.sin(a0);
            const x1 = C + R * Math.cos(a1), y1 = C + R * Math.sin(a1);
            const mid = ((i + 0.5) * seg - 90) * (Math.PI) / 180;
            const tx = C + 92 * Math.cos(mid), ty = C + 92 * Math.sin(mid);
            const blank = s.name === BLANK_NAME;
            const hasImg = !blank && !!s.image_url;
            const IR = 22; // segment thumbnail radius
            return (
              <g key={i}>
                <path d={`M ${C} ${C} L ${x0} ${y0} A ${R} ${R} 0 0 1 ${x1} ${y1} Z`}
                  fill={blank ? "#0B0D12" : i % 2 ? "#191307" : "#101318"}
                  stroke="rgba(212,175,55,.45)" strokeWidth="1.5" />
                {/* Name label — primary content for blank/no-image segments, and the
                    safe fallback that stays visible behind a missing/broken thumbnail. */}
                <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle"
                  transform={`rotate(${(i + 0.5) * seg}, ${tx}, ${ty})`}
                  fill={blank ? "#5B6472" : GOLD} fontSize="9" fontWeight="700" letterSpacing="1">
                  {(s.name.length > 16 ? s.name.slice(0, 15) + "…" : s.name).toUpperCase()}
                </text>
                {hasImg && (
                  <g role="img" aria-label={s.name}>
                    <title>{s.name}</title>
                    <clipPath id={`ld-seg-clip-${i}`}>
                      <circle cx={tx} cy={ty} r={IR} />
                    </clipPath>
                    <image href={s.image_url} x={tx - IR} y={ty - IR} width={IR * 2} height={IR * 2}
                      preserveAspectRatio="xMidYMid slice" clipPath={`url(#ld-seg-clip-${i})`} />
                    <circle cx={tx} cy={ty} r={IR} fill="none" stroke="rgba(212,175,55,.55)" strokeWidth="1.5" />
                  </g>
                )}
              </g>
            );
          })}
          <circle cx={C} cy={C} r="34" fill="#0A0C10" stroke={GOLD} strokeWidth="2" />
          <text x={C} y={C - 4} textAnchor="middle" fill={GOLD} fontSize="10" fontWeight="800" letterSpacing="2">PLUS</text>
          <text x={C} y={C + 9} textAnchor="middle" fill={GOLD} fontSize="10" fontWeight="800" letterSpacing="2">MAKER</text>
        </svg>
      </div>
    </div>
  );
}

const emptyAddress = { recipient_name: "", phone: "", email: "", country: "", state: "", city: "", postal_code: "", address_line1: "", address_line2: "" };

export default function MemberLuckyDraw() {
  const navigate = useNavigate();
  const [feedbackMine, setFeedbackMine] = useState({});
  useEffect(() => { api.get("/member/feedback/mine").then((res) => setFeedbackMine(res.data || {})).catch(() => {}); }, []);
  const [data, setData] = useState(null);
  const [spinTier, setSpinTier] = useState(null);       // tier being spun
  const [segments, setSegments] = useState([]);
  const [targetIndex, setTargetIndex] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);           // server result
  const [addrPrize, setAddrPrize] = useState(null);
  const [addr, setAddr] = useState(emptyAddress);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/member/lucky-draw");
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const openSpin = (tier) => {
    const segs = tier.rewards.map((r) => ({ id: r.id, name: r.name, image_url: r.image_url }));
    if (tier.blank_weight > 0) segs.push({ id: null, name: BLANK_NAME });
    setSegments(segs);
    setSpinTier(tier);
    setResult(null);
    setTargetIndex(null);
  };

  const doSpin = async () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    try {
      const res = await api.post("/member/lucky-draw/spin", { tier_code: spinTier.code });
      const d = res.data;
      let idx = segments.findIndex((s) => s.id === d.reward_id);
      if (d.result_type === "blank") {
        idx = segments.findIndex((s) => s.id === null);
        if (idx < 0) idx = 0;
      }
      if (idx < 0) idx = 0;
      setResult(d);
      setTargetIndex(idx);
    } catch (err) {
      setSpinning(false);
      setSpinTier(null);
      toast.error(formatApiError(err));
    }
  };

  const onWheelDone = () => {
    setSpinning(false);
    load();
  };

  const closeSpin = () => { if (!spinning) { setSpinTier(null); setResult(null); setTargetIndex(null); } };

  const openAddress = (prize) => {
    const saved = data?.saved_address || {};
    setAddr({ ...emptyAddress, ...saved });
    setAddrPrize(prize);
  };

  const submitAddress = async () => {
    setBusy("addr");
    try {
      await api.post(`/member/lucky-draw/prizes/${addrPrize.id}/address`, addr);
      toast.success("Delivery address submitted — your prize is now processing.");
      setAddrPrize(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy("");
  };

  const tiers = data?.tiers || [];
  const prizes = data?.prizes || [];
  const spins = data?.spins || [];
  const tierName = (code) => tiers.find((t) => t.code === code)?.name || `${code} DRAW`;

  return (
    <div className="max-w-6xl mx-auto space-y-10" data-testid="member-lucky-draw-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-[0.15em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>
            PLUSMAKER LUCKY DRAW
          </h1>
          <p className="text-sm text-[#8A93A6] mt-2">Win exclusive physical rewards. Rank milestones unlock premium draws and tickets.</p>
        </div>
        <div className="px-4 py-2 rounded-xl border border-[#D4AF37]/25 bg-[#101318] text-sm" data-testid="current-rank-level">
          <span className="text-[#8A93A6]">Your highest rank level:</span>{" "}
          <span className="text-[#E9C766] font-bold">{data?.current_level ?? "—"}</span>
        </div>
      </div>

      {/* ---------- TIERS ---------- */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {tiers.map((t) => (
          <div key={t.code} data-testid={`tier-card-${t.code}`}
            className={`rounded-2xl border p-6 space-y-4 transition-colors ${t.unlocked ? "border-[#D4AF37]/35 bg-[#101318]" : "border-white/5 bg-[#0C0E13] opacity-80"}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-base font-bold tracking-[0.12em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>{t.name}</div>
                <div className="text-xs text-[#8A93A6] mt-1">Unlocks at {t.unlock_rank_name || `rank level ${t.unlock_level}`}</div>
              </div>
              {t.unlocked
                ? <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-[#D4AF37]/40 text-[#E9C766]"><Ticket size={12} /> <span data-testid={`ticket-balance-${t.code}`}>{t.ticket_balance}</span>&nbsp;ticket{t.ticket_balance === 1 ? "" : "s"}</span>
                : <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-white/10 text-[#64748B]"><Lock size={12} /> LOCKED</span>}
            </div>
            {t.just_unlocked && <div className="text-xs text-emerald-300 flex items-center gap-1.5" data-testid={`tier-just-unlocked-${t.code}`}><Sparkles size={12} /> Just unlocked — tickets added!</div>}
            <div className="flex gap-2 flex-wrap min-h-[40px]">
              {t.rewards.length === 0 && t.unlocked && <span className="text-xs text-[#64748B]">Rewards coming soon.</span>}
              {t.rewards.slice(0, 4).map((r) => (
                <div key={r.id} className="w-10 h-10 rounded-lg border border-[#D4AF37]/20 bg-[#0A0C10] overflow-hidden" title={r.name}>
                  {r.image_url ? <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" /> : <Gift className="m-2.5 text-[#D4AF37]/60" size={18} />}
                </div>
              ))}
              {t.rewards.length > 4 && <span className="text-xs text-[#8A93A6] self-center">+{t.rewards.length - 4} more</span>}
            </div>
            {t.unlocked ? (
              <button data-testid={`spin-btn-${t.code}`} onClick={() => openSpin(t)}
                disabled={t.ticket_balance < 1 || t.rewards.length === 0 || !t.active}
                className="w-full py-2.5 rounded-xl text-sm font-bold tracking-[0.15em] bg-gradient-to-r from-[#B8860B] to-[#E9C766] text-black disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-110 transition">
                {t.ticket_balance < 1 ? "NO TICKETS" : t.rewards.length === 0 ? "NO REWARDS IN STOCK" : "SPIN NOW"}
              </button>
            ) : (
              <div className="text-center text-xs text-[#64748B] py-2.5 rounded-xl border border-white/5" data-testid={`tier-requirement-${t.code}`}>
                REQUIRES RANK: {t.unlock_rank_name || `RANK LEVEL ${t.unlock_level}`}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ---------- MY PRIZES ---------- */}
      <section data-testid="my-prizes-section">
        <h2 className="text-lg font-bold tracking-[0.15em] text-[#E9C766] flex items-center gap-2" style={{ fontFamily: "Cinzel, serif" }}>
          <Package size={18} /> MY PRIZES
        </h2>
        {prizes.length === 0 ? (
          <p className="text-sm text-[#64748B] mt-3">No prizes yet — unlock a draw and spin to win.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {prizes.map((p) => (
              <div key={p.id} data-testid={`prize-card-${p.id}`} className="rounded-2xl border border-[#D4AF37]/20 bg-[#101318] p-5 flex gap-4">
                <div className="w-20 h-20 rounded-xl overflow-hidden border border-[#D4AF37]/25 bg-[#0A0C10] shrink-0">
                  {p.image_url ? <img src={p.image_url} alt={p.reward_name} className="w-full h-full object-cover" /> : <Gift className="m-6 text-[#D4AF37]/60" size={30} />}
                </div>
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm text-white">{p.reward_name}</div>
                    <span data-testid={`prize-status-${p.id}`} className={`text-[10px] px-2 py-0.5 rounded-full border font-bold tracking-wider ${STATUS_STYLE[p.status]}`}>{p.status.replace("_", " ")}</span>
                  </div>
                  <div className="text-xs text-[#8A93A6]">{tierName(p.tier_code)} · Won {fmtMYT(p.won_at, false)}</div>
                  {p.tracking_number && <div className="text-xs text-[#E9C766] flex items-center gap-1.5" data-testid={`prize-tracking-${p.id}`}><Truck size={12} /> Tracking: {p.tracking_number}</div>}
                  {p.status === "COMPLETED" && (
                    feedbackMine[`lucky_draw:${p.id}`] ? (
                      <button data-testid={`prize-view-feedback-${p.id}`} onClick={() => navigate(`/member/feedback?highlight=${feedbackMine[`lucky_draw:${p.id}`].id}`)}
                        className="mt-1 text-xs px-3 py-1.5 rounded-lg border border-[#2a2d38] text-[#94A3B8] hover:text-white transition flex items-center gap-1.5">
                        <MessageSquareText size={12} /> View Feedback
                      </button>
                    ) : (
                      <button data-testid={`prize-feedback-btn-${p.id}`} onClick={() => navigate(`/member/feedback/new?source=lucky_draw&id=${p.id}`)}
                        className="mt-1 text-xs px-3 py-1.5 rounded-lg border border-[#D4AF37]/40 text-[#E9C766] hover:bg-[#D4AF37]/10 transition flex items-center gap-1.5">
                        <MessageSquareText size={12} /> Feedback
                      </button>
                    )
                  )}
                  {(p.status === "PENDING_ADDRESS" || p.status === "PROCESSING") && (
                    <button data-testid={`prize-address-btn-${p.id}`} onClick={() => openAddress(p)}
                      className="mt-1 text-xs px-3 py-1.5 rounded-lg border border-[#D4AF37]/40 text-[#E9C766] hover:bg-[#D4AF37]/10 transition flex items-center gap-1.5">
                      <MapPin size={12} /> {p.status === "PENDING_ADDRESS" ? "Confirm Delivery Address" : "Update Delivery Address"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---------- HISTORY ---------- */}
      <section data-testid="draw-history-section">
        <h2 className="text-lg font-bold tracking-[0.15em] text-[#E9C766] flex items-center gap-2" style={{ fontFamily: "Cinzel, serif" }}>
          <History size={18} /> DRAW HISTORY
        </h2>
        {spins.length === 0 ? (
          <p className="text-sm text-[#64748B] mt-3">No spins yet.</p>
        ) : (
          <div className="mt-4 rounded-2xl border border-white/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#101318] text-[#8A93A6] text-xs tracking-wider">
                <tr><th className="text-left px-4 py-3">DATE</th><th className="text-left px-4 py-3">DRAW</th><th className="text-left px-4 py-3">RESULT</th></tr>
              </thead>
              <tbody>
                {spins.map((s) => (
                  <tr key={s.spin_id} data-testid={`spin-row-${s.spin_id}`} className="border-t border-white/5">
                    <td className="px-4 py-3 text-[#8A93A6]">{fmtMYT(s.created_at)}</td>
                    <td className="px-4 py-3 text-white">{tierName(s.tier_code)}</td>
                    <td className="px-4 py-3">
                      {s.result_type === "win"
                        ? <span className="text-[#E9C766] font-semibold">Won {s.reward_name}</span>
                        : <span className="text-[#64748B]">Better luck next time</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ---------- OFFICIAL RULES DISCLOSURE ---------- */}
      <div className="rounded-2xl border border-[#222530] bg-[#0A0C10] p-6 space-y-2" data-testid="lucky-draw-rules-disclosure">
        <p className="text-[10px] font-mono tracking-[0.25em] text-[#64748B] uppercase">Official Rules</p>
        <p className="text-xs text-[#8A93A6] leading-relaxed">
          Entries are earned through rank progress — tickets are awarded at rank milestones. No purchase is necessary to enter or win.
          Eligibility, age and geographic restrictions may apply. Prizes and availability are subject to the official rules.
          Apple and Google are not sponsors, endorsers or administrators of, and are not involved in, this promotion.{" "}
          <a href="/lucky-draw-rules" className="text-[#D4AF37] hover:text-[#E5C158]" data-testid="lucky-draw-rules-link">Read the full official rules</a>.
        </p>
      </div>

      {/* ---------- SPIN MODAL ---------- */}
      {spinTier && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="spin-wheel-modal">
          <div className="w-full max-w-md rounded-3xl border border-[#D4AF37]/30 bg-[#0C0E13] p-8 space-y-6 relative">
            <button onClick={closeSpin} disabled={spinning} className="absolute top-4 right-4 text-[#64748B] hover:text-white disabled:opacity-30" data-testid="spin-close-btn"><X size={18} /></button>
            <div className="text-center">
              <div className="text-lg font-bold tracking-[0.2em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>{spinTier.name}</div>
              <div className="text-xs text-[#8A93A6] mt-1">1 ticket per spin · result is decided securely by the server</div>
            </div>
            <Wheel segments={segments} targetIndex={targetIndex} spinning={spinning && targetIndex != null} onDone={onWheelDone} />
            {!result && !spinning && (
              <button onClick={doSpin} data-testid="spin-confirm-btn"
                className="w-full py-3 rounded-xl font-bold tracking-[0.2em] bg-gradient-to-r from-[#B8860B] to-[#E9C766] text-black hover:brightness-110 transition flex items-center justify-center gap-2">
                <Dices size={18} /> SPIN
              </button>
            )}
            {spinning && <div className="text-center text-sm text-[#E9C766] animate-pulse" data-testid="spinning-indicator">The wheel is deciding your fortune…</div>}
            {result && !spinning && (
              <div className="text-center space-y-4" data-testid="spin-result">
                {result.result_type === "win" ? (
                  <>
                    <div className="text-xl font-bold text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>CONGRATULATIONS!</div>
                    {result.prize?.image_url && <img src={result.prize.image_url} alt={result.prize.reward_name} className="w-24 h-24 object-cover rounded-xl mx-auto border border-[#D4AF37]/40" data-testid="spin-result-image" />}
                    <div className="text-white font-semibold" data-testid="spin-result-name">{result.prize?.reward_name}</div>
                    <div className="text-xs text-[#8A93A6]">Won {fmtMYT(result.prize?.won_at)} · Confirm your delivery address in My Prizes.</div>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-bold text-[#8A93A6]" data-testid="spin-result-blank">BETTER LUCK NEXT TIME</div>
                    <div className="text-xs text-[#64748B]">Your next milestone brings more tickets.</div>
                  </>
                )}
                <button onClick={closeSpin} data-testid="spin-result-close-btn" className="px-6 py-2 rounded-xl border border-[#D4AF37]/40 text-[#E9C766] text-sm hover:bg-[#D4AF37]/10 transition">CLOSE</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- ADDRESS MODAL ---------- */}
      {addrPrize && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="prize-address-modal">
          <div className="w-full max-w-lg rounded-3xl border border-[#D4AF37]/30 bg-[#0C0E13] p-8 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-base font-bold tracking-[0.15em] text-[#E9C766]" style={{ fontFamily: "Cinzel, serif" }}>DELIVERY ADDRESS</div>
                <div className="text-xs text-[#8A93A6] mt-1">{addrPrize.reward_name} · {tierName(addrPrize.tier_code)}</div>
              </div>
              <button onClick={() => setAddrPrize(null)} className="text-[#64748B] hover:text-white" data-testid="address-close-btn"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["recipient_name", "Recipient Name"], ["phone", "Phone"], ["email", "Email"], ["country", "Country"],
                ["state", "State"], ["city", "City"], ["postal_code", "Postal Code"], ["address_line1", "Address Line 1"], ["address_line2", "Address Line 2 (optional)"],
              ].map(([k, label]) => (
                <div key={k} className={k.startsWith("address") ? "sm:col-span-2" : ""}>
                  <label className="text-xs text-[#8A93A6] tracking-wider">{label}</label>
                  <input data-testid={`addr-${k}`} value={addr[k]} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })}
                    className="mt-1 w-full bg-[#101318] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37]/50 outline-none" />
                </div>
              ))}
            </div>
            <button onClick={submitAddress} disabled={busy === "addr"} data-testid="address-submit-btn"
              className="w-full py-2.5 rounded-xl font-bold tracking-[0.15em] bg-gradient-to-r from-[#B8860B] to-[#E9C766] text-black disabled:opacity-40 hover:brightness-110 transition">
              {busy === "addr" ? "SUBMITTING…" : "CONFIRM ADDRESS"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
